import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useLocation, Link } from 'react-router-dom';
import { SlidersHorizontal, X, ChevronRight, Check } from 'lucide-react';
import api from '@/lib/api';
import { inr, cx } from '@/lib/format';
import ProductCard from '@/components/common/ProductCard';
import Loader, { EmptyState } from '@/components/common/Loader';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const SORTS = [
  { v: 'featured', l: 'Featured' },
  { v: 'newest', l: 'Newest' },
  { v: 'price_asc', l: 'Price: Low to High' },
  { v: 'price_desc', l: 'Price: High to Low' },
  { v: 'best_selling', l: 'Best Selling' },
  { v: 'discount', l: 'Discount' },
  { v: 'rating', l: 'Customer Rating' },
];

const titleCase = (s) => (s || '').split('-').map((w) => w[0]?.toUpperCase() + w.slice(1)).join(' ');

export default function Shop() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const location = useLocation();
  const mode = location.pathname.split('/')[1]; // shop|category|style|material|festival|collection
  const q = sp.get('q') || '';
  const qpColor = sp.get('color') || '';
  const qpBudget = sp.get('budget') || '';
  const qpRoom = sp.get('room') || '';

  const [facets, setFacets] = useState(null);
  const [data, setData] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  const [sort, setSort] = useState('featured');
  const [price, setPrice] = useState([0, 15000]);
  const [material, setMaterial] = useState('');
  const [color, setColor] = useState('');
  const [style, setStyle] = useState('');
  const [rating, setRating] = useState(0);
  const [inStock, setInStock] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get('/facets').then((r) => { setFacets(r.data); setPrice([r.data.price_min, r.data.price_max]); }).catch(() => {});
  }, []);

  // Fetch the managed category document (hero, description, SEO) for taxonomy pages
  useEffect(() => {
    if (mode === 'shop' || !slug) { setCategory(null); return; }
    let alive = true;
    api.get(`/categories/${slug}`, { params: { type: mode } })
      .then((r) => { if (alive) setCategory(r.data); })
      .catch(() => { if (alive) setCategory(null); });
    return () => { alive = false; };
  }, [slug, mode]);

  // SEO — prefer the category's own metadata, fall back to admin defaults
  useEffect(() => {
    const applyMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const setCanonical = (href) => {
      let el = document.querySelector('link[rel="canonical"]');
      if (href) {
        if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
        el.setAttribute('href', href);
      } else if (el) { el.remove(); }
    };

    if (category?.seo) {
      const s = category.seo;
      document.title = s.meta_title || `${category.name} · Gaze Decor`;
      applyMeta('description', s.meta_description);
      applyMeta('keywords', s.keywords);
      applyMeta('robots', s.robots);
      setCanonical(s.canonical_url);
    } else {
      api.get('/home').then((r) => {
        const seo = r.data?.seo || {};
        const label = slug ? slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Shop';
        document.title = seo.meta_title ? `${label} · ${seo.meta_title}` : `${label} · Gaze Decor`;
        applyMeta('description', seo.meta_description);
      }).catch(() => {});
    }
    return () => { document.title = 'Gaze Decor'; setCanonical(''); };
  }, [slug, category]);

  // reset when route changes
  useEffect(() => { setPage(1); setMaterial(''); setColor(''); setStyle(''); setRating(0); setInStock(false); }, [slug, mode, q]);

  const heading = useMemo(() => {
    if (q) return `Results for "${q}"`;
    if (mode === 'shop') return 'All Products';
    if (category?.name) return category.name;
    return titleCase(slug);
  }, [mode, slug, q, category]);

  useEffect(() => {
    if (!facets) return;
    setLoading(true);
    const params = { sort, page, limit: 24, min_price: price[0], max_price: price[1] };
    if (q) params.q = q;
    if (mode === 'category') params.category = slug;
    if (mode === 'style') params.style = slug;
    if (mode === 'material') params.material = slug;
    if (mode === 'festival') params.festival = slug;
    if (mode === 'collection') params.collection = slug;
    if (material) params.material = material;
    if (color) params.color = color; else if (qpColor) params.color = qpColor;
    if (qpBudget) params.budget = qpBudget;
    if (qpRoom) params.room = qpRoom;
    if (style) params.style = style.toLowerCase();
    if (rating) params.min_rating = rating;
    if (inStock) params.availability = 'in_stock';
    api.get('/products', { params }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [facets, sort, page, price, material, color, style, rating, inStock, slug, mode, q, qpColor, qpBudget, qpRoom]);

  const activeChips = [
    material && { label: material, clear: () => setMaterial('') },
    color && { label: color, clear: () => setColor('') },
    style && { label: style, clear: () => setStyle('') },
    rating > 0 && { label: `${rating}★ & up`, clear: () => setRating(0) },
    inStock && { label: 'In stock', clear: () => setInStock(false) },
  ].filter(Boolean);

  const Filters = () => (
    <div className="space-y-1">
      <Accordion type="multiple" defaultValue={['price', 'material', 'color', 'style', 'rating']} className="w-full">
        <AccordionItem value="price">
          <AccordionTrigger className="text-sm font-semibold">Price</AccordionTrigger>
          <AccordionContent>
            <div className="px-1 pt-2">
              <Slider value={price} min={facets?.price_min || 0} max={facets?.price_max || 15000} step={100} onValueChange={setPrice} data-testid="filter-price-slider" />
              <div className="mt-3 flex justify-between text-xs text-muted-foreground"><span>{inr(price[0])}</span><span>{inr(price[1])}</span></div>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="material">
          <AccordionTrigger className="text-sm font-semibold">Material</AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-wrap gap-2">
              {(facets?.materials || []).map((m) => (
                <button key={m} onClick={() => setMaterial(material === m ? '' : m)} className={cx('rounded-full border px-3 py-1.5 text-xs', material === m ? 'border-[hsl(var(--gaze-gold))] bg-[hsl(var(--gaze-ivory))]' : 'border-border bg-white')}>{m}</button>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="color">
          <AccordionTrigger className="text-sm font-semibold">Color</AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-wrap gap-3">
              {(facets?.colors || []).map((c) => (
                <button key={c.name} onClick={() => setColor(color === c.name ? '' : c.name)} title={c.name} className="flex flex-col items-center gap-1">
                  <span className={cx('grid h-8 w-8 place-items-center rounded-full border', color === c.name ? 'ring-2 ring-[hsl(var(--gaze-gold))] ring-offset-2' : 'border-black/10')} style={{ background: c.hex }}>
                    {color === c.name && <Check size={14} className={c.name === 'White' || c.name === 'Ivory' ? 'text-black' : 'text-white'} />}
                  </span>
                  <span className="text-[10px] text-muted-foreground">{c.name}</span>
                </button>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="style">
          <AccordionTrigger className="text-sm font-semibold">Style</AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-wrap gap-2">
              {(facets?.styles || []).map((s) => (
                <button key={s} onClick={() => setStyle(style === s ? '' : s)} className={cx('rounded-full border px-3 py-1.5 text-xs', style === s ? 'border-[hsl(var(--gaze-gold))] bg-[hsl(var(--gaze-ivory))]' : 'border-border bg-white')}>{s}</button>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="rating">
          <AccordionTrigger className="text-sm font-semibold">Customer Rating</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-1">
              {[4, 3, 2].map((r) => (
                <button key={r} onClick={() => setRating(rating === r ? 0 : r)} className={cx('flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm', rating === r ? 'bg-[hsl(var(--gaze-ivory))]' : '')}>
                  <span className="text-[hsl(var(--gaze-gold))]">{'★'.repeat(r)}<span className="text-muted-foreground/40">{'★'.repeat(5 - r)}</span></span> & up
                </button>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <label className="mt-2 flex cursor-pointer items-center gap-2 border-t border-border py-3 text-sm">
        <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} data-testid="filter-in-stock" className="h-4 w-4 accent-[hsl(var(--primary))]" /> In stock only
      </label>
    </div>
  );

  const heroImg = category?.hero_image || '';
  const heroImgMobile = category?.hero_image_mobile || category?.hero_image || '';
  const catDesc = category?.description || '';
  const catShort = category?.short_description || '';

  return (
    <div>
      {/* Category hero banner (managed via admin) */}
      {heroImg && (
        <section className="relative overflow-hidden" data-testid="category-hero">
          <div className="absolute inset-0">
            <img src={heroImg} alt={heading} className="hidden h-full w-full object-cover md:block" />
            <img src={heroImgMobile} alt={heading} className="h-full w-full object-cover md:hidden" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />
          </div>
          <div className="container-gaze relative flex min-h-[220px] flex-col justify-end py-8 sm:min-h-[300px]">
            <h1 className="font-display text-3xl text-white sm:text-5xl" data-testid="category-hero-title">{heading}</h1>
            {catShort && <p className="mt-2 max-w-xl text-sm text-white/85 sm:text-base">{catShort}</p>}
          </div>
        </section>
      )}

      {/* Breadcrumb + heading (heading hidden when hero already shows it) */}
      <div className="border-b border-border bg-white">
        <div className="container-gaze py-6">
          <nav className="mb-2 flex items-center gap-1 text-xs text-muted-foreground" data-testid="breadcrumb">
            <Link to="/" className="hover:text-foreground">Home</Link><ChevronRight size={12} />
            <span className="capitalize">{mode}</span>{slug && (<><ChevronRight size={12} /><span className="text-foreground">{titleCase(slug)}</span></>)}
          </nav>
          {!heroImg && <h1 className="text-3xl font-semibold sm:text-4xl">{heading}</h1>}
          {catDesc && <p className={cx('max-w-3xl text-sm leading-relaxed text-muted-foreground', heroImg ? '' : 'mt-3')} data-testid="category-description">{catDesc}</p>}
        </div>
      </div>

      <div className="container-gaze py-6">
        <div className="flex gap-8">
          {/* Desktop sidebar */}
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-24"><p className="mb-3 text-sm font-semibold">Filters</p><Filters /></div>
          </aside>

          <div className="min-w-0 flex-1">
            {/* Toolbar */}
            <div className="mb-5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button onClick={() => setFilterOpen(true)} data-testid="plp-filter-open-button" className="flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium lg:hidden">
                  <SlidersHorizontal size={16} /> Filters{activeChips.length > 0 && ` (${activeChips.length})`}
                </button>
                <p className="hidden text-sm text-muted-foreground sm:block">{data?.total ?? 0} products</p>
              </div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-[190px]" data-testid="plp-sort-button"><SelectValue /></SelectTrigger>
                <SelectContent>{SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {activeChips.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {activeChips.map((c, i) => (
                  <button key={i} onClick={c.clear} className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--gaze-gold))]/50 bg-white px-3 py-1.5 text-xs">{c.label} <X size={12} /></button>
                ))}
              </div>
            )}

            {loading ? <Loader label="Loading products" /> : (data?.items?.length ? (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {data.items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                </div>
                {data.pages > 1 && (
                  <div className="mt-10 flex items-center justify-center gap-2">
                    {Array.from({ length: data.pages }).slice(0, 8).map((_, i) => (
                      <button key={i} onClick={() => setPage(i + 1)} className={cx('h-9 w-9 rounded-full text-sm', page === i + 1 ? 'bg-[hsl(var(--primary))] text-white' : 'border border-border bg-white')}>{i + 1}</button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <EmptyState icon={SlidersHorizontal} title="No products found" subtitle="Try adjusting your filters or explore our collections." />
            ))}
          </div>
        </div>
      </div>

      {/* Mobile filter sheet */}
      <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
        <SheetContent side="left" className="w-[88%] max-w-sm overflow-y-auto">
          <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
          <div className="mt-4 pb-24"><Filters /></div>
          <div className="absolute inset-x-0 bottom-0 flex gap-3 border-t border-border bg-white p-4">
            <button onClick={() => { setMaterial(''); setColor(''); setStyle(''); setRating(0); setInStock(false); }} className="flex-1 rounded-full border border-border py-3 text-sm font-semibold">Clear</button>
            <button onClick={() => setFilterOpen(false)} className="flex-1 rounded-full bg-[hsl(var(--primary))] py-3 text-sm font-semibold text-white">Show {data?.total ?? 0}</button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
